/**
 * Phase 8: Lower sticky controls - show mode, Preload/Transition, optional full-panel entry.
 * Live context is toggled via the SIMULATION/LIVE switch on the Control tab.
 */

import { useMemo, type ReactNode } from 'react';
import { useTranslations } from '@/i18n/use-translations';
import { Box, Divider, IconButton, Paper, SvgIcon, Tooltip, type SxProps, type Theme } from '@mui/material';
import { MdViewAgenda, MdViewDay, MdViewHeadline } from 'react-icons/md';
import { PreloadTransitionButtons } from '../preload-transition-buttons';
import { PerformShowModeRow } from './perform-show-mode-row';
import { PerformTransportBar } from './perform-transport-bar';
import { PerformCategoryFooterButtons } from './perform-category-footer-buttons';
import { RemoteSpeakersOnStageToggle } from './remote-speakers-on-stage-toggle';
import { PerformEqualSpreadIconRow, performEqualSpreadIconButtonSx } from './perform-equal-spread-icon-row';
import { useFeatureAccess } from '@/hooks/use-feature-access';
import type { FlowDto } from '@/types/dtos/flows/response/flow.dto';
import type { LiveContextStateDto } from '@/state/queries/player/use-get-live-context-state';
import { LiveTranslationControlButton } from './flow-control-live-translation-panel';
import { collectFlowMedia, findStagedMediaProcessingBlock } from '@/helpers/media-processing-insight.helper';

import type { FlowControlPerformMode } from '@/types/player/simulation-cue-sheet.types';
import type { FlowControlNarrowViewMode } from '@/types/player/flow-control-narrow-view.types';

export interface PerformLowerControlsProps {
  flow: FlowDto;
  entityRef: string;
  eventSlotId?: string;
  performMode?: FlowControlPerformMode;
  liveContextState?: LiveContextStateDto;
  /** React Query `dataUpdatedAt` for live-context GET — seeds perform-control WS store on each fresh snapshot. */
  liveContextQueryUpdatedAt?: number;
  /** When true, show legacy show-mode row (normally overlays on previews). Default false. */
  showShowModeRow?: boolean;
  /** Narrow Control tab: show List / Program+List / Program+Preview mode switcher. */
  showNarrowViewModes?: boolean;
  narrowViewMode?: FlowControlNarrowViewMode;
  onNarrowViewModeChange?: (mode: FlowControlNarrowViewMode) => void;
  /** When false, Paper is not position:sticky (e.g. already placed in a flex footer). Default true. */
  stickyFooter?: boolean;
  /**
   * Narrow Control footer (list + overlay): drop left/right Paper padding so the bar
   * aligns with the page edge in both modes. Bottom padding is kept for browser chrome.
   */
  flushEdges?: boolean;
}

export function PerformLowerControls({
  flow,
  entityRef,
  eventSlotId,
  performMode = 'play',
  liveContextState,
  liveContextQueryUpdatedAt,
  showShowModeRow = false,
  showNarrowViewModes = false,
  narrowViewMode = 'list',
  onNarrowViewModeChange,
  stickyFooter = true,
  flushEdges = false,
}: PerformLowerControlsProps) {
  const t = useTranslations();
  const { hasAccess: hasAutoCue } = useFeatureAccess('perform_auto_cue');

  const selectedItemIds = liveContextState?.stagedSelection?.selectedItemIds ?? [];
  const processingBlock = useMemo(
    () => findStagedMediaProcessingBlock(selectedItemIds, collectFlowMedia(flow)),
    [selectedItemIds, flow],
  );
  const showTransport = hasAutoCue || performMode === 'simulation';
  const translationButton = performMode === 'play' ? <LiveTranslationControlButton entityRef={entityRef} /> : null;

  const modeButtonSx = (active: boolean) => ({
    ...performEqualSpreadIconButtonSx,
    color: active ? 'primary.main' : undefined,
  });

  const showCategoryButtons = hasAutoCue && !!flow.liveContext?.isRunning;
  const showNarrowModes = showNarrowViewModes && !!onNarrowViewModeChange;

  const transportAndSettings = showTransport ? (
    <PerformTransportBar
      flow={flow}
      entityRef={entityRef}
      performMode={performMode}
      liveContextState={liveContextState}
      liveContextQueryUpdatedAt={liveContextQueryUpdatedAt}
      equalSpreadOnMobile
      endAdornment={translationButton}
    />
  ) : (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <PreloadTransitionButtons
        entityRef={entityRef}
        liveContextState={liveContextState}
        selectedItemIds={selectedItemIds}
        processingBlock={processingBlock}
      />
      {translationButton}
    </Box>
  );

  const inputsAndOnStage = (
    <Box sx={{ display: 'flex', alignItems: 'center', width: { xs: '100%', sm: 'auto' }, minWidth: 0 }}>
      <PerformCategoryFooterButtons
        flow={flow}
        eventSlotId={eventSlotId}
        liveContextState={liveContextState}
        equalSpreadOnMobile
      />
      {performMode === 'play' ? (
        <RemoteSpeakersOnStageToggle
          flow={flow}
          entityRef={entityRef}
          eventSlotId={eventSlotId}
          liveContextState={liveContextState}
        />
      ) : null}
    </Box>
  );

  const narrowModeButtons = showNarrowModes ? (
    <PerformEqualSpreadIconRow>
      <Tooltip title={t('flows.detail.perform.lowerControls.viewMode.list', { defaultValue: 'Schedule list' })}>
        <IconButton
          size="medium"
          aria-label={t('flows.detail.perform.lowerControls.viewMode.list', {
            defaultValue: 'Schedule list',
          })}
          aria-pressed={narrowViewMode === 'list'}
          onClick={() => onNarrowViewModeChange('list')}
          sx={modeButtonSx(narrowViewMode === 'list')}
        >
          <SvgIcon component={MdViewHeadline} inheritViewBox />
        </IconButton>
      </Tooltip>
      <Tooltip
        title={t('flows.detail.perform.lowerControls.viewMode.programWithList', {
          defaultValue: 'Program with list',
        })}
      >
        <IconButton
          size="medium"
          aria-label={t('flows.detail.perform.lowerControls.viewMode.programWithList', {
            defaultValue: 'Program with list',
          })}
          aria-pressed={narrowViewMode === 'programWithList'}
          onClick={() => onNarrowViewModeChange('programWithList')}
          sx={modeButtonSx(narrowViewMode === 'programWithList')}
        >
          <SvgIcon component={MdViewDay} inheritViewBox />
        </IconButton>
      </Tooltip>
      <Tooltip
        title={t('flows.detail.perform.lowerControls.viewMode.programAndPreview', {
          defaultValue: 'Program and preview',
        })}
      >
        <IconButton
          size="medium"
          aria-label={t('flows.detail.perform.lowerControls.viewMode.programAndPreview', {
            defaultValue: 'Program and preview',
          })}
          aria-pressed={narrowViewMode === 'programAndPreview'}
          onClick={() => onNarrowViewModeChange('programAndPreview')}
          sx={modeButtonSx(narrowViewMode === 'programAndPreview')}
        >
          <SvgIcon component={MdViewAgenda} inheritViewBox />
        </IconButton>
      </Tooltip>
    </PerformEqualSpreadIconRow>
  ) : null;

  return (
    <Paper
      elevation={flushEdges ? 0 : 1}
      sx={{
        ...(stickyFooter
          ? {
              position: 'sticky',
              bottom: 0,
              left: 0,
              right: 0,
              zIndex: 10,
              mt: 1,
            }
          : { position: 'relative', mt: 0 }),
        pt: 1,
        pb: 1,
        px: flushEdges ? 0 : { xs: 1, sm: 2 },
        ...(flushEdges
          ? {
              borderRadius: 0,
              boxShadow: 'none',
              bgcolor: 'transparent',
            }
          : {}),
      }}
    >
      {showShowModeRow && (
        <>
          <CenteredDividerRow
            left={transportAndSettings}
            right={
              <PerformShowModeRow
                flow={flow}
                entityRef={entityRef}
                performMode={performMode}
                liveContextState={liveContextState}
                equalSpreadOnMobile
                sx={{ mb: 0 }}
              />
            }
          />
          <Divider sx={{ my: 0.5 }} />
        </>
      )}

      {showCategoryButtons && showTransport && !showShowModeRow ? (
        showNarrowModes ? (
          <ThreeSegmentDividerRow left={narrowModeButtons} center={inputsAndOnStage} right={transportAndSettings} />
        ) : (
          <CenteredDividerRow left={inputsAndOnStage} right={transportAndSettings} />
        )
      ) : (
        <CenteredDividerRow left={narrowModeButtons} right={!showShowModeRow ? transportAndSettings : null} />
      )}
    </Paper>
  );
}

const segmentCellSx = {
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  gap: { xs: 0.5, sm: 1 },
  minWidth: 0,
  alignSelf: 'stretch',
  px: { xs: 1, sm: 2 },
} as const;

/** Vertical divider centered in the row; equal flex space on each side absorbs leftover width as gaps. */
function CenteredDividerRow({ left, right, sx }: { left: ReactNode; right: ReactNode; sx?: SxProps<Theme> }) {
  const hasLeft = left != null;
  const hasRight = right != null;
  if (!hasLeft && !hasRight) {
    return null;
  }
  if (!hasLeft || !hasRight) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          minWidth: 0,
          ...sx,
        }}
      >
        {left ?? right}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        minWidth: 0,
        ...sx,
      }}
    >
      <Box
        sx={{
          ...segmentCellSx,
          justifyContent: { xs: 'stretch', sm: 'flex-end' },
        }}
      >
        {left}
      </Box>
      <Divider orientation="vertical" flexItem sx={{ alignSelf: 'stretch', mx: 0, flexShrink: 0 }} />
      <Box
        sx={{
          ...segmentCellSx,
          justifyContent: { xs: 'stretch', sm: 'flex-start' },
        }}
      >
        {right}
      </Box>
    </Box>
  );
}

/** Three equal segments for narrow Control: view modes | LiveKit inputs | cue transport. */
function ThreeSegmentDividerRow({
  left,
  center,
  right,
  sx,
}: {
  left: ReactNode;
  center: ReactNode;
  right: ReactNode;
  sx?: SxProps<Theme>;
}) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        minWidth: 0,
        ...sx,
      }}
    >
      <Box sx={{ ...segmentCellSx, justifyContent: { xs: 'stretch', sm: 'flex-end' } }}>{left}</Box>
      <Divider orientation="vertical" flexItem sx={{ alignSelf: 'stretch', mx: 0, flexShrink: 0 }} />
      <Box sx={{ ...segmentCellSx, justifyContent: 'center', flex: { xs: '0 0 auto', sm: 1 } }}>{center}</Box>
      <Divider orientation="vertical" flexItem sx={{ alignSelf: 'stretch', mx: 0, flexShrink: 0 }} />
      <Box sx={{ ...segmentCellSx, justifyContent: { xs: 'stretch', sm: 'flex-start' } }}>{right}</Box>
    </Box>
  );
}
